/**
 * Поле «Отходы по ФККО» карточки строки (ecology-fkko, файл заказчика 23.09.2026: «при внесении кода
 * ФККО система автоматически подставляет название отхода из справочника», и наоборот). Список пар
 * «код ФККО + наименование отхода» — у паспортов отходов их может быть много.
 *
 * Оба поля пары — с подсказками справочника (`GET /api/ecology/fkko?q=`): по цифрам — коды с таким
 * началом, по словам — наименования. Выбор подсказки заполняет пару целиком; полностью введённый
 * код, который есть в справочнике, сам подставляет пустое наименование, а наименование, совпавшее
 * со справочником дословно, — пустой код. Справочник только помогает: пару можно ввести вручную, и
 * пустой справочник поле не блокирует (решение Р13 плана).
 *
 * Список сохраняется кнопкой карточки вместе с остальными полями; проверки — зеркало
 * `domain.NormalizeEcoFKKOEntries` (код вида отхода — 11 цифр, последняя — класс опасности 1–5, код
 * группы каталога не принимается; наименование обязательно; код не повторяется). В подсказках рядом
 * с наименованием — класс опасности.
 *
 * То же поле — в заявке на паспорта отходов (ecology-order-fields-files-fkko): там код
 * необязателен («можно указать любое название отхода»), указанный проверяется как обычно.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X } from 'lucide-react'
import { ecologyApi, type EcoCardField, type EcoFkkoEntry } from '@/lib/api'
import { fkkoHazardClass, formatFkkoCode, normalizeFkkoCode } from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { useComboboxEscape } from '@/company/use-combobox-escape'
import { useWorkScaleClass } from '@/design-system/work-scale'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'

/** Отход в форме: `uid` — стабильный ключ строки списка (правка не пересоздаёт поля ввода). */
export interface FkkoRow {
  uid: string
  code: string
  name: string
}

let rowSeq = 0
/** Ключ новой строки списка. */
export const newFkkoRowUid = () => `fkko-new-${++rowSeq}`

/** Строки формы из сохранённых отходов: ключ — по позиции, одинаковый при каждом построении. */
export function fkkoRowsFrom(entries: EcoFkkoEntry[]): FkkoRow[] {
  return entries.map((e, i) => ({ uid: `fkko-${i}`, code: formatFkkoCode(e.code), name: e.name }))
}

/** Наименование без лишних пробелов — как сохранит сервер. */
const collapse = (s: string) => s.trim().replace(/\s+/g, ' ')

/**
 * Проверка списка — зеркало сервера. Ошибки по «номер.code» / «номер.name» (номер с нуля), как в
 * ответе сервера `fkko.<ключ>.<номер>.<поле>`. `codeOptional` — код можно не указывать (заявка).
 */
export function fkkoRowErrors(
  rows: FkkoRow[],
  { codeOptional = false }: { codeOptional?: boolean } = {},
): Record<string, string> {
  const out: Record<string, string> = {}
  const seen = new Set<string>()
  rows.forEach((r, i) => {
    const code = normalizeFkkoCode(r.code)
    if (codeOptional && !r.code.trim()) {
      if (!collapse(r.name)) out[`${i}.name`] = 'Укажите наименование отхода'
      return
    }
    if (!code) out[`${i}.code`] = 'Код ФККО — 11 цифр'
    else if (!fkkoHazardClass(code))
      out[`${i}.code`] = 'Это код группы отходов — укажите код вида отхода'
    else if (seen.has(code)) out[`${i}.code`] = 'Этот код уже есть в списке'
    if (code) seen.add(code)
    if (!collapse(r.name)) out[`${i}.name`] = 'Укажите наименование отхода'
  })
  return out
}

/** Список для сервера: код цифрами, наименование без лишних пробелов. */
export function fkkoEntriesFrom(rows: FkkoRow[]): EcoFkkoEntry[] {
  return rows.map((r) => ({
    code: normalizeFkkoCode(r.code) ?? r.code.trim(),
    name: collapse(r.name),
  }))
}

export function FkkoListField({
  field,
  rows,
  errors,
  disabled,
  onChange,
}: {
  /** Поле карточки или заявки: подпись и подсказка. */
  field: Pick<EcoCardField, 'label' | 'hint'>
  rows: FkkoRow[]
  /** Ошибки по «номер.code» / «номер.name». */
  errors: Record<string, string>
  disabled: boolean
  onChange: (rows: FkkoRow[]) => void
}) {
  const patch = (uid: string, next: Partial<FkkoRow>) =>
    onChange(rows.map((r) => (r.uid === uid ? { ...r, ...next } : r)))

  return (
    <fieldset className="my-4 min-w-0 border-0 p-0">
      <legend className="mb-1.5 text-13 text-txt-secondary">{field.label}</legend>
      {field.hint ? <p className="mb-2 text-12 text-txt-tertiary">{field.hint}</p> : null}
      {rows.length === 0 ? (
        <p className="mb-2 text-13 text-txt-tertiary">Отходы не указаны</p>
      ) : (
        <ol className="mb-2 flex flex-col gap-2.5">
          {rows.map((row, i) => {
            const codeError = errors[`${i}.code`]
            const nameError = errors[`${i}.name`]
            return (
              <li key={row.uid} className="flex items-start gap-2">
                <div className="w-44 shrink-0">
                  <FkkoSuggestInput
                    mode="code"
                    value={row.code}
                    ariaLabel={`Код ФККО, отход ${i + 1}`}
                    placeholder="0 00 000 00 00 0"
                    invalid={Boolean(codeError)}
                    disabled={disabled}
                    onChange={(code) => patch(row.uid, { code })}
                    onPick={(e) => patch(row.uid, { code: formatFkkoCode(e.code), name: e.name })}
                    onExact={(e) => {
                      if (!collapse(row.name)) patch(row.uid, { name: e.name })
                    }}
                  />
                  {codeError ? (
                    <p role="alert" className="mt-1 text-12 text-red">
                      {codeError}
                    </p>
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <FkkoSuggestInput
                    mode="name"
                    value={row.name}
                    ariaLabel={`Наименование отхода ${i + 1}`}
                    placeholder="Наименование отхода"
                    invalid={Boolean(nameError)}
                    disabled={disabled}
                    onChange={(name) => patch(row.uid, { name })}
                    onPick={(e) => patch(row.uid, { code: formatFkkoCode(e.code), name: e.name })}
                    onExact={(e) => {
                      if (!row.code.trim()) patch(row.uid, { code: formatFkkoCode(e.code) })
                    }}
                  />
                  {nameError ? (
                    <p role="alert" className="mt-1 text-12 text-red">
                      {nameError}
                    </p>
                  ) : null}
                </div>
                {disabled ? null : (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="mt-0.5 shrink-0"
                    aria-label={`Убрать отход ${i + 1}`}
                    title="Убрать отход"
                    onClick={() => onChange(rows.filter((r) => r.uid !== row.uid))}
                  >
                    <X className="size-4 stroke-current" aria-hidden />
                  </Button>
                )}
              </li>
            )
          })}
        </ol>
      )}
      {disabled ? null : (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          leftIcon={<Plus className="size-3.5 stroke-current" aria-hidden />}
          onClick={() => onChange([...rows, { uid: newFkkoRowUid(), code: '', name: '' }])}
        >
          Добавить отход
        </Button>
      )}
    </fieldset>
  )
}

/**
 * Поле пары с подсказками справочника ФККО (combobox по WAI-ARIA: ↑/↓ — выбор, Enter — подставить,
 * Esc — закрыть список, не закрывая карточку). Список — в портале, как у `SuggestInput`: у нижнего
 * края модалки не обрезается. Сбой поиска подсказок поле не ломает — ввод остаётся ручным.
 */
function FkkoSuggestInput({
  mode,
  value,
  ariaLabel,
  placeholder,
  invalid,
  disabled,
  onChange,
  onPick,
  onExact,
}: {
  /** По какому полю пары ищем: код (цифры) или наименование (слова). */
  mode: 'code' | 'name'
  value: string
  ariaLabel: string
  placeholder: string
  invalid: boolean
  disabled: boolean
  onChange: (value: string) => void
  /** Выбрана подсказка — пара заполняется целиком. */
  onPick: (entry: EcoFkkoEntry) => void
  /** Введённое совпало со справочником дословно — можно подставить вторую половину пары. */
  onExact: (entry: EcoFkkoEntry) => void
}) {
  const listboxId = useId()
  const [items, setItems] = useState<EcoFkkoEntry[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  // Подсказки ищем только по вводу пользователя, а не по значению, подставленному из пары.
  const [typed, setTyped] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const workScale = useWorkScaleClass()
  // onExact — из родителя, новый на каждый рендер: держим в ref, чтобы не перезапускать поиск.
  const onExactRef = useRef(onExact)
  useEffect(() => {
    onExactRef.current = onExact
  })

  useEffect(() => () => clearTimeout(blurTimer.current ?? undefined), [])

  useEffect(() => {
    if (!typed) return
    const q = value.trim()
    const digits = q.replace(/[\s-]/g, '')
    const enough = mode === 'code' ? /^\d{2,11}$/.test(digits) : q.length >= 3
    if (!enough) {
      setItems([])
      return
    }
    const controller = new AbortController()
    const handle = setTimeout(() => {
      ecologyApi
        .searchFkko(mode === 'code' ? digits : q, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return
          setItems(res.items)
          setActive(-1)
          const exact =
            mode === 'code'
              ? res.items.find((e) => e.code === digits)
              : res.items.find((e) => e.name.toLowerCase() === collapse(q).toLowerCase())
          if (exact) onExactRef.current(exact)
        })
        // Подсказки — вспомогательные: сбой или отмена не мешают ручному вводу.
        .catch(() => undefined)
    }, 200)
    return () => {
      clearTimeout(handle)
      controller.abort()
    }
  }, [mode, typed, value])

  const visible = open && items.length > 0
  const close = () => {
    setOpen(false)
    setActive(-1)
  }
  useComboboxEscape(visible, anchorRef, close)

  useLayoutEffect(() => {
    if (!visible) return
    const update = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      // Список наименований шире поля кода: не уже 24rem, но в пределах окна.
      const width = Math.min(Math.max(r.width, 384), window.innerWidth - r.left - 16)
      setPos({ top: r.bottom + 4, left: r.left, width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [visible])

  const pick = (entry: EcoFkkoEntry) => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    setTyped(false)
    setItems([])
    close()
    onPick(entry)
  }
  const optionId = (i: number) => `${listboxId}-${i}`

  return (
    <div ref={anchorRef}>
      <Input
        value={value}
        placeholder={placeholder}
        invalid={invalid}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        inputMode={mode === 'code' ? 'numeric' : undefined}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={visible ? listboxId : undefined}
        aria-activedescendant={visible && active >= 0 ? optionId(active) : undefined}
        onChange={(e) => {
          setTyped(true)
          setOpen(true)
          onChange(e.target.value)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(close, 150)
        }}
        onKeyDown={(e) => {
          if (!visible) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, items.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter' && active >= 0) {
            // Enter без выбранной подсказки не перехватываем — он отправляет форму.
            e.preventDefault()
            pick(items[active])
          }
        }}
      />
      {visible && pos
        ? createPortal(
            <ul
              id={listboxId}
              role="listbox"
              aria-label="Подсказки справочника ФККО"
              className={cn(
                'ds-surface pointer-events-auto fixed z-popover max-h-56 overflow-auto rounded-8 py-1 shadow-elevated',
                workScale,
              )}
              // pointerEvents inline: модальный Radix Dialog ставит pointer-events:none на <body>.
              style={{ top: pos.top, left: pos.left, width: pos.width, pointerEvents: 'auto' }}
            >
              {items.map((entry, i) => (
                // Клавиатура — у поля ввода (aria-activedescendant), опция откликается на клик.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  key={entry.code}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === active}
                  className={cn(
                    'flex cursor-pointer items-baseline gap-3 px-3 py-2 text-13 text-txt hover:bg-hover',
                    i === active && 'bg-hover',
                  )}
                  // Фокус остаётся в поле: blur-таймер не закрывает список до onClick.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(entry)}
                >
                  <span className="shrink-0 tabular-nums text-txt-secondary">
                    {formatFkkoCode(entry.code)}
                  </span>
                  <span className="min-w-0 flex-1">{entry.name}</span>
                  {fkkoHazardClass(entry.code) ? (
                    <span className="shrink-0 whitespace-nowrap text-12 text-txt-tertiary">
                      {fkkoHazardClass(entry.code)} класс
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}
